import React, { useState, useEffect, useCallback, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, ArrowLeft, BookOpen, Feather } from "lucide-react";
import { BOOK, getNarrationText } from "../data/book";
import Sketch from "../components/book/Sketch";
import NarrationPlayer from "../components/book/NarrationPlayer";
import ReadingLume from "../components/book/ReadingLume";
import LightParticles from "../components/book/LightParticles";
import { createLead } from "../lib/api";
import { usePageTitle } from "../lib/usePageTitle";
import { useIsMobile } from "../lib/useIsMobile";

const EASE = [0.16, 1, 0.3, 1];

/* Page-curl flip: leaving page hinges on its outer edge,
   incoming page swings in from the opposite outer edge. */
const flip = {
  enter: (dir) => ({
    rotateY: dir > 0 ? -95 : 95,
    opacity: 0.2,
    filter: "brightness(0.85)",
    transformOrigin: dir > 0 ? "left center" : "right center",
  }),
  center: {
    rotateY: 0,
    opacity: 1,
    filter: "brightness(1)",
    transformOrigin: "center center",
  },
  exit: (dir) => ({
    rotateY: dir > 0 ? 95 : -95,
    opacity: 0.15,
    filter: "brightness(0.8)",
    transformOrigin: dir > 0 ? "right center" : "left center",
  }),
};

const fade = {
  hidden: { opacity: 0, y: 16 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.7, delay: 0.35 + i * 0.12, ease: EASE } }),
};

function DropCapParagraph({ text, cap }) {
  if (!cap) return <p className="font-serif text-base sm:text-lg md:text-2xl leading-[1.5] sm:leading-[1.45] text-ink mb-4">{text}</p>;
  const first = text.charAt(0);
  return (
    <p className="font-serif text-base sm:text-lg md:text-2xl leading-[1.5] sm:leading-[1.45] text-ink mb-4">
      <span className="float-left font-serif text-[2.6rem] sm:text-[3.4rem] leading-[0.8] pr-2 sm:pr-3 pt-1 text-forest">{first}</span>
      {text.slice(1)}
    </p>
  );
}

/* ---------- individual page layouts ---------- */

function Cover({ page }) {
  return (
    <div className="h-full flex flex-col items-center justify-center text-center px-6 sm:px-8 pt-14 sm:pt-8">
      <motion.div variants={fade} initial="hidden" animate="show" custom={0}>
        <BookOpen className="h-7 w-7 text-brass mx-auto" />
      </motion.div>
      <motion.p variants={fade} initial="hidden" animate="show" custom={1} className="mt-6 sm:mt-8 font-sans text-[10px] sm:text-xs uppercase tracking-mega text-brass">
        Read slowly · one page at a time
      </motion.p>
      <motion.h1 variants={fade} initial="hidden" animate="show" custom={2} className="mt-5 sm:mt-6 font-serif text-5xl sm:text-6xl md:text-8xl leading-[0.95] text-ink">
        {page.title}
      </motion.h1>
      <motion.p variants={fade} initial="hidden" animate="show" custom={3} className="mt-6 sm:mt-8 font-serif italic text-xl sm:text-2xl md:text-3xl text-ink-muted max-w-lg">
        {page.subtitle}
      </motion.p>
      <motion.div variants={fade} initial="hidden" animate="show" custom={4} className="mt-8 sm:mt-12 h-px w-16 bg-line" />
      <motion.div variants={fade} initial="hidden" animate="show" custom={5} className="mt-6 sm:mt-8">
        <div className="relative mx-auto h-20 w-20 sm:h-24 sm:w-24 md:h-28 md:w-28 rounded-full overflow-hidden ring-1 ring-line shadow-[0_10px_30px_-12px_rgba(28,28,28,0.35)]">
          <img
            src="/founder/krishna-portrait-2026.jpg?v=3"
            alt="Krishna S, founder of Get Virtual Growth"
            className="h-full w-full object-cover object-[50%_28%]"
            data-testid="book-cover-portrait"
          />
        </div>
      </motion.div>
      <motion.p variants={fade} initial="hidden" animate="show" custom={5} className="mt-4 sm:mt-5 font-serif text-2xl sm:text-3xl text-forest">{page.author}</motion.p>
      <motion.p variants={fade} initial="hidden" animate="show" custom={5} className="font-sans text-[10px] sm:text-xs uppercase tracking-mega text-ink-muted mt-2">{page.role}</motion.p>
      <motion.p variants={fade} initial="hidden" animate="show" custom={6} className="mt-6 sm:mt-10 font-sans text-[10px] sm:text-xs uppercase tracking-wider text-ink-muted/70">
        A four-minute read · best enjoyed in one sitting
      </motion.p>
    </div>
  );
}

function Chapter({ page }) {
  return (
    <div className="h-full overflow-y-auto no-scrollbar grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-12 items-start md:items-center px-6 sm:px-8 md:px-14 pt-16 md:pt-10 pb-10">
      <div className="md:col-span-7">
        {page.num && (
          <motion.p variants={fade} initial="hidden" animate="show" custom={0} className="font-sans text-[10px] sm:text-xs uppercase tracking-mega text-brass mb-3">
            Chapter {page.num}
          </motion.p>
        )}
        <motion.h2 variants={fade} initial="hidden" animate="show" custom={1} className="font-serif text-3xl sm:text-4xl md:text-5xl leading-[1.05] text-ink mb-6 md:mb-8">
          {page.title}
        </motion.h2>
        <motion.div variants={fade} initial="hidden" animate="show" custom={2}>
          {page.body.map((t, i) => (
            <DropCapParagraph key={i} text={t} cap={i === 0} />
          ))}
        </motion.div>
      </div>
      {page.illo && (
        <motion.div variants={fade} initial="hidden" animate="show" custom={2} className="md:col-span-5 h-40 md:h-72">
          <Sketch name={page.illo} />
        </motion.div>
      )}
    </div>
  );
}

function Gratitude({ page }) {
  return (
    <div className="h-full overflow-y-auto no-scrollbar flex flex-col items-center justify-center text-center px-6 sm:px-10 pt-14 sm:pt-8 pb-8">
      <motion.div variants={fade} initial="hidden" animate="show" custom={0}>
        <Feather className="h-7 w-7 sm:h-8 sm:w-8 text-brass mx-auto" />
      </motion.div>
      <motion.h2 variants={fade} initial="hidden" animate="show" custom={1} className="mt-6 sm:mt-8 font-serif text-4xl sm:text-5xl md:text-6xl text-ink">
        {page.title}
      </motion.h2>
      <motion.div variants={fade} initial="hidden" animate="show" custom={2} className="mt-5 sm:mt-6 max-w-xl">
        {page.body.map((t, i) => (
          <p key={i} className="font-serif text-xl sm:text-2xl md:text-3xl leading-[1.5] text-ink-muted mb-3 sm:mb-4">{t}</p>
        ))}
      </motion.div>
    </div>
  );
}

function Expect({ page }) {
  return (
    <div className="h-full overflow-y-auto no-scrollbar flex flex-col justify-start md:justify-center px-6 sm:px-8 md:px-14 pt-16 md:pt-10 pb-10">
      <motion.p variants={fade} initial="hidden" animate="show" custom={0} className="font-sans text-[10px] sm:text-xs uppercase tracking-mega text-brass mb-3">{page.subtitle}</motion.p>
      <motion.h2 variants={fade} initial="hidden" animate="show" custom={1} className="font-serif text-3xl sm:text-4xl md:text-5xl text-ink mb-8 md:mb-10">{page.title}</motion.h2>
      <div className="space-y-6 sm:space-y-7">
        {page.steps.map((s, i) => (
          <motion.div key={s.k} variants={fade} initial="hidden" animate="show" custom={2 + i} className="flex gap-4 sm:gap-6 items-start">
            <span className="font-serif text-3xl sm:text-4xl text-forest/40 leading-none">{s.k}</span>
            <div>
              <h3 className="font-serif text-xl sm:text-2xl md:text-3xl text-ink">{s.t}</h3>
              <p className="mt-1 font-sans text-sm sm:text-base text-ink-muted leading-relaxed max-w-md">{s.d}</p>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  );
}

function Close({ page }) {
  const navigate = useNavigate();
  return (
    <div className="h-full overflow-y-auto no-scrollbar flex flex-col items-center justify-center text-center px-6 sm:px-10 pt-14 sm:pt-8 pb-8">
      <motion.h2 variants={fade} initial="hidden" animate="show" custom={0} className="font-serif text-4xl sm:text-5xl md:text-7xl leading-[1.0] text-ink max-w-2xl">
        {page.title}
      </motion.h2>
      <motion.div variants={fade} initial="hidden" animate="show" custom={1} className="mt-6 sm:mt-8 max-w-lg">
        {page.body.map((t, i) => (
          <p key={i} className="font-serif italic text-xl sm:text-2xl md:text-3xl text-ink-muted mb-3">{t}</p>
        ))}
      </motion.div>
      <motion.div variants={fade} initial="hidden" animate="show" custom={2} className="mt-8 sm:mt-10">
        <p className="font-serif text-lg sm:text-xl text-ink-muted">{page.signoff}</p>
        <p className="font-serif italic text-4xl sm:text-5xl md:text-6xl text-forest leading-none mt-1">{page.author}</p>
        <p className="font-sans text-[10px] sm:text-xs uppercase tracking-mega text-ink-muted mt-3">{page.role}</p>
      </motion.div>
      <motion.button
        variants={fade} initial="hidden" animate="show" custom={3}
        onClick={() => navigate("/")}
        data-testid="book-to-diagnosis"
        className="group mt-8 sm:mt-10 inline-flex items-center gap-3 rounded-full bg-forest px-6 sm:px-8 py-3 sm:py-4 font-sans text-xs uppercase tracking-wider font-semibold text-bg hover:bg-forest-hover transition-colors"
      >
        Start my free diagnosis
        <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
      </motion.button>
    </div>
  );
}

function ValuePage({ page }) {
  return (
    <div className="h-full overflow-y-auto no-scrollbar px-6 sm:px-8 md:px-14 pt-16 md:pt-10 pb-10 flex flex-col justify-start md:justify-center">
      <motion.p variants={fade} initial="hidden" animate="show" custom={0} className="font-sans text-[10px] sm:text-xs uppercase tracking-mega text-brass mb-3">{page.subtitle}</motion.p>
      <motion.h2 variants={fade} initial="hidden" animate="show" custom={1} className="font-serif text-3xl sm:text-4xl md:text-5xl text-ink mb-6 md:mb-8">{page.title}</motion.h2>
      <div className="space-y-4 sm:space-y-5">
        {page.items.map((it, i) => (
          <motion.div key={it.n} variants={fade} initial="hidden" animate="show" custom={2 + i} className="flex gap-4 sm:gap-5 items-baseline border-b border-line pb-3 sm:pb-4">
            <span className="font-sans text-[10px] sm:text-xs tracking-mega text-forest/50 pt-1">{it.n}</span>
            <div>
              <h3 className="font-serif text-xl sm:text-2xl md:text-3xl text-ink">{it.name}</h3>
              <p className="mt-1 font-sans text-sm sm:text-base text-ink-muted leading-relaxed max-w-xl">{it.why}</p>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  );
}

function MathsPage({ page }) {
  return (
    <div className="h-full overflow-y-auto no-scrollbar px-6 sm:px-8 md:px-14 pt-16 md:pt-10 pb-10 flex flex-col justify-start md:justify-center">
      <motion.p variants={fade} initial="hidden" animate="show" custom={0} className="font-sans text-[10px] sm:text-xs uppercase tracking-mega text-brass mb-3">{page.subtitle}</motion.p>
      <motion.h2 variants={fade} initial="hidden" animate="show" custom={1} className="font-serif text-3xl sm:text-4xl md:text-5xl text-ink mb-6 md:mb-8">{page.title}</motion.h2>
      <div className="space-y-3">
        {page.rows.map((r, i) => (
          <motion.div key={i} variants={fade} initial="hidden" animate="show" custom={2 + i} className="flex items-baseline justify-between gap-3 sm:gap-4 border-b border-line pb-3">
            <p className="font-serif text-lg sm:text-xl md:text-2xl text-ink">{r.item}</p>
            {r.note && <p className="font-sans text-xs sm:text-sm text-ink-muted whitespace-nowrap">{r.note}</p>}
          </motion.div>
        ))}
      </div>
      <motion.p variants={fade} initial="hidden" animate="show" custom={7} className="mt-6 sm:mt-7 font-serif italic text-xl sm:text-2xl md:text-3xl leading-snug text-ink">
        {page.punch}
      </motion.p>
    </div>
  );
}

function FormPage({ page, onDone }) {
  const [form, setForm] = useState({ name: "", email: "", phone: "", business_name: "", website: "", challenge: "" });
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const cls = "w-full bg-transparent border-b border-line py-2.5 font-sans text-base text-ink placeholder:text-ink-muted/40 outline-none focus:border-forest transition-colors";

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone || !form.business_name) {
      toast.error("Please share your name, email, phone and business name.");
      return;
    }
    setLoading(true);
    try {
      await createLead({ ...form, source: "book" });
      toast.success("Thank you. Krishna will review your business.");
      onDone();
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="h-full overflow-y-auto no-scrollbar px-6 sm:px-8 md:px-14 pt-16 md:pt-10 pb-10 flex flex-col justify-start md:justify-center">
      <motion.p variants={fade} initial="hidden" animate="show" custom={0} className="font-sans text-[10px] sm:text-xs uppercase tracking-mega text-brass mb-3">Your turn</motion.p>
      <motion.h2 variants={fade} initial="hidden" animate="show" custom={1} className="font-serif text-3xl sm:text-4xl md:text-5xl text-ink">{page.title}</motion.h2>
      <motion.p variants={fade} initial="hidden" animate="show" custom={1} className="mt-3 font-sans text-sm sm:text-base text-ink-muted">{page.subtitle}</motion.p>
      <motion.form variants={fade} initial="hidden" animate="show" custom={2} onSubmit={submit} className="mt-6 sm:mt-8" data-testid="book-lead-form">
        <div className="grid md:grid-cols-2 gap-x-8 gap-y-4 sm:gap-y-5">
          <input data-testid="book-name" className={cls} placeholder="Your name" value={form.name} onChange={set("name")} />
          <input data-testid="book-email" type="email" className={cls} placeholder="Email" value={form.email} onChange={set("email")} />
          <input data-testid="book-phone" className={cls} placeholder="Phone / WhatsApp" value={form.phone} onChange={set("phone")} />
          <input data-testid="book-business" className={cls} placeholder="Business name" value={form.business_name} onChange={set("business_name")} />
          <input data-testid="book-website" className={cls} placeholder="Website / Google listing" value={form.website} onChange={set("website")} />
          <input data-testid="book-challenge" className={cls} placeholder="Biggest thing slowing your growth" value={form.challenge} onChange={set("challenge")} />
        </div>
        <button type="submit" disabled={loading} data-testid="book-submit" className="group mt-8 inline-flex items-center gap-3 rounded-full bg-forest px-8 py-4 font-sans text-xs uppercase tracking-wider font-semibold text-bg hover:bg-forest-hover transition-colors disabled:opacity-60">
          {loading ? "Sending..." : "Send Krishna my details"}
          <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
        </button>
        <p className="mt-3 font-sans text-xs text-ink-muted">Complimentary and confidential. No contract, no strings attached.</p>
      </motion.form>
    </div>
  );
}

/* ---------- the reader shell ---------- */

export default function Book() {
  usePageTitle("The Book");
  const isMobile = useIsMobile(768);
  const [[index, dir], setState] = useState([0, 0]);
  const total = BOOK.length;
  const page = BOOK[index];
  const stageRef = useRef(null);
  const [mobileScrollProgress, setMobileScrollProgress] = useState(0);

  const go = useCallback((d) => {
    setState(([i]) => {
      const next = i + d;
      if (next < 0 || next >= total) return [i, 0];
      return [next, d];
    });
  }, [total]);

  useEffect(() => {
    if (isMobile) return;
    const onKey = (e) => {
      if (e.key === "ArrowRight") go(1);
      if (e.key === "ArrowLeft") go(-1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [go, isMobile]);

  useEffect(() => {
    if (!isMobile) return;
    const onScroll = () => {
      const h = document.documentElement;
      const max = (h.scrollHeight - h.clientHeight) || 1;
      setMobileScrollProgress(Math.max(0, Math.min(1, h.scrollTop / max)));
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [isMobile]);

  const progress = total > 1 ? (index / (total - 1)) * 100 : 0;
  const atEnd = index === total - 1;
  const atStart = index === 0;

  const renderPage = (p) => {
    switch (p.type) {
      case "cover": return <Cover page={p} />;
      case "gratitude": return <Gratitude page={p} />;
      case "expect": return <Expect page={p} />;
      case "value": return <ValuePage page={p} />;
      case "maths": return <MathsPage page={p} />;
      case "close": return <Close page={p} />;
      case "form": return <FormPage page={p} onDone={() => window.scrollTo({ top: 0, behavior: "smooth" })} />;
      default: return <Chapter page={p} />;
    }
  };

  const render = () => renderPage(page);

  /* ================================================================ */
  /*  Mobile · static vertical stack                                   */
  /* ================================================================ */
  if (isMobile) {
    return (
      <main
        className="relative min-h-screen bg-black pt-20 pb-24 overflow-x-hidden gvg-typography"
        data-testid="book-page-mobile"
      >
        <LightParticles count={18} />
        <div className="pointer-events-none absolute inset-0 opacity-[0.35] bg-[radial-gradient(circle_at_15%_10%,rgba(201,169,97,0.10),transparent_55%),radial-gradient(circle_at_85%_90%,rgba(201,169,97,0.06),transparent_60%)]" />

        {/* Sticky scroll-progress rail */}
        <div className="fixed top-16 inset-x-0 h-px bg-line/60 z-30">
          <motion.div
            className="h-full bg-brass origin-left"
            style={{ scaleX: mobileScrollProgress }}
          />
        </div>

        <div className="relative z-10 flex flex-col gap-6 px-4">
          {BOOK.map((p, i) => (
            <article
              key={p.id}
              data-testid={`book-mobile-page-${i}`}
              className="relative rounded-2xl border border-line bg-surface shadow-[0_20px_60px_-20px_rgba(0,0,0,0.9)] overflow-hidden"
              style={{ minHeight: "80vh" }}
            >
              {/* Content layer */}
              <div className="relative h-full w-full">
                {getNarrationText(p) && (
                  <NarrationPlayer
                    pageId={p.id}
                    pageKey={p.id}
                    text={getNarrationText(p)}
                  />
                )}
                {renderPage(p)}
                {p.type !== "cover" && (
                  <span className="absolute bottom-3 right-4 font-sans text-[10px] tracking-wider text-ink-muted/70">
                    {i} / {BOOK.length - 1}
                  </span>
                )}
              </div>

              {/* Faint page-hint below content (not on last) */}
              {p.hint && i < BOOK.length - 1 && (
                <div className="pointer-events-none absolute inset-x-0 bottom-0 flex justify-center pb-3">
                  <p className="font-serif italic text-xs text-ink-muted/60 text-center px-6">
                    {p.hint}
                  </p>
                </div>
              )}
            </article>
          ))}
        </div>
      </main>
    );
  }

  /* ================================================================ */
  /*  Desktop · original 3D page-flip experience                       */
  /* ================================================================ */
  return (
    <main className="fixed inset-0 pt-20 flex flex-col bg-black overflow-hidden gvg-typography" data-testid="book-page">
      {/* ambient light particles across full page */}
      <LightParticles count={35} />

      {/* faint warm vignettes */}
      <div className="pointer-events-none absolute inset-0 opacity-[0.35] bg-[radial-gradient(circle_at_15%_10%,rgba(201,169,97,0.10),transparent_55%),radial-gradient(circle_at_85%_90%,rgba(201,169,97,0.06),transparent_60%)]" />

      {/* progress */}
      <div className="relative h-px bg-line mx-6 md:mx-12 z-10">
        <motion.div
          className="absolute left-0 top-0 h-full bg-brass"
          animate={{ width: `${progress}%` }}
          transition={{ duration: 0.9, ease: EASE }}
        />
      </div>

      {/* stage */}
      <div
        ref={stageRef}
        className="relative flex-1 flex items-center justify-center px-3 sm:px-4 md:px-8 py-4 sm:py-6 z-10"
        style={{ perspective: 2400 }}
      >
        <ReadingLume containerRef={stageRef} />
        <div className="relative w-full max-w-4xl h-[76vh] sm:h-[74vh] md:h-[74vh]">
          <AnimatePresence custom={dir} mode="wait">
            <motion.div
              key={page.id}
              custom={dir}
              variants={flip}
              initial="enter"
              animate="center"
              exit="exit"
              transition={{ duration: 0.9, ease: EASE }}
              style={{ transformStyle: "preserve-3d", willChange: "transform, opacity" }}
              className="book-sheet absolute inset-0 rounded-2xl border border-line bg-surface shadow-[0_50px_120px_-30px_rgba(0,0,0,0.85)] overflow-hidden"
              data-testid={`book-page-${index}`}
            >
              {/* Content layer */}
              <div className="relative h-full w-full">
                {getNarrationText(page) && (
                  <NarrationPlayer
                    pageId={page.id}
                    pageKey={page.id}
                    text={getNarrationText(page)}
                  />
                )}
                {render()}
                {page.type !== "cover" && (
                  <span className="absolute bottom-4 right-6 font-sans text-xs tracking-wider text-ink-muted/70">
                    {index} / {total - 1}
                  </span>
                )}
              </div>

              {/* Center-fold trough (always on) */}
              <div className="book-fold pointer-events-none absolute inset-0" aria-hidden="true" />

              {/* Reading Lume overlay */}
              <div className="book-lume pointer-events-none absolute inset-0" aria-hidden="true" />

              {/* Inner shadow rim (paper thickness) */}
              <div className="pointer-events-none absolute inset-0 rounded-2xl shadow-[inset_0_0_60px_rgba(0,0,0,0.55)]" aria-hidden="true" />
            </motion.div>
          </AnimatePresence>
        </div>
      </div>

      {/* controls */}
      <div className="relative pb-5 sm:pb-6 md:pb-8 px-4 sm:px-6 md:px-12 flex items-center justify-between gap-3 z-10">
        <button
          onClick={() => go(-1)}
          disabled={atStart}
          data-testid="book-prev"
          className={`inline-flex items-center gap-1.5 sm:gap-2 font-sans text-xs sm:text-sm shrink-0 ${atStart ? "opacity-0 pointer-events-none" : "text-ink-muted hover:text-brass"}`}
          style={{ transition: "color 500ms cubic-bezier(0.16, 1, 0.3, 1)" }}
        >
          <ArrowLeft className="h-4 w-4" /> <span className="hidden sm:inline">Previous</span><span className="sm:hidden">Back</span>
        </button>

        <p className="hidden md:block font-serif italic text-lg md:text-xl text-ink-muted text-center px-4 flex-1" data-testid="book-hint">
          {page.hint}
        </p>

        {!atEnd ? (
          <button
            onClick={() => go(1)}
            data-testid="book-next"
            className="group inline-flex items-center gap-2 rounded-full bg-forest px-5 sm:px-6 py-2.5 sm:py-3 font-sans text-[11px] sm:text-xs uppercase tracking-wider font-semibold text-bg hover:bg-forest-hover shrink-0"
            style={{ transition: "background-color 500ms cubic-bezier(0.16, 1, 0.3, 1), transform 500ms cubic-bezier(0.16, 1, 0.3, 1)" }}
          >
            {index === 0 ? "Open" : "Turn the page"}
            <ArrowRight className="h-4 w-4 transition-transform duration-500 group-hover:translate-x-1" />
          </button>
        ) : (
          <span className="w-[110px]" />
        )}
      </div>
    </main>
  );
}